import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trash2, Pencil, Star, Eye, EyeOff, Sparkles, Loader2, ExternalLink, Wand2, Store, Layers, RefreshCw, Plus } from "lucide-react";
import { api } from "../../lib/api";
import { Modal, IconBtn, SearchBox, AddBtn, inputCls, CATEGORIES, cedis } from "./ui";
import BulkImportModal from "./BulkImport";

export default function Catalog({ fx }) {
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [seg, setSeg] = useState("all");
  const [form, setForm] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [remap, setRemap] = useState({ running: false, done: 0, total: 0 });
  const [sel, setSel] = useState(new Set());
  const [bulkTag, setBulkTag] = useState("");
  const [repricing, setRepricing] = useState(new Set());

  const reprice = async (p) => {
    setRepricing((s) => new Set(s).add(p.id));
    try {
      const { data } = await api.post(`/admin/products/${p.id}/reprice`);
      if (data.price_found) {
        toast.success(data.new_price_gbp !== data.old_price_gbp
          ? `Price updated £${data.old_price_gbp} → £${data.new_price_gbp}`
          : "Price unchanged — refreshed");
      } else if (!data.fetched) {
        toast.message("Source page couldn't be read (site may block bots) — marked as checked today");
      } else toast.message("No price found on the page — marked as checked today");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refresh failed"); }
    finally { setRepricing((s) => { const n = new Set(s); n.delete(p.id); return n; }); }
  };

  const load = async () => {
    const [p, s, c] = await Promise.all([api.get("/admin/products"), api.get("/admin/stores"), api.get("/admin/categories")]);
    setProducts(p.data); setStores(s.data); setCategories(c.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    let t;
    const poll = async () => {
      try { const { data } = await api.get("/admin/ai/remap-status"); setRemap(data); if (data.running) t = setTimeout(poll, 2500); else load(); }
      catch { /* ignore */ }
    };
    poll();
    return () => clearTimeout(t);
  }, []);

  const runRemap = async () => {
    if (!window.confirm("AI will re-classify and tag every product into your master categories. Continue?")) return;
    try {
      const { data } = await api.post("/admin/ai/remap-catalog"); toast.success(`Re-mapping ${data.total} products…`); setRemap({ running: true, done: 0, total: data.total });
      const poll = async () => { const { data: st } = await api.get("/admin/ai/remap-status"); setRemap(st); if (st.running) setTimeout(poll, 2500); else { toast.success("Catalogue re-mapped & tagged"); load(); } };
      setTimeout(poll, 2500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start re-map"); }
  };

  const list = useMemo(() => products.filter((p) => {
    if (seg === "published" && p.status === "draft") return false;
    if (seg === "draft" && p.status !== "draft") return false;
    return `${p.name} ${p.brand} ${p.category} ${p.source_store || ""}`.toLowerCase().includes(q.toLowerCase());
  }), [products, q, seg, ]);

  const draftCount = products.filter((p) => p.status === "draft").length;
  const save = async (p, patch) => {
    const body = fullBody({ ...p, ...patch });
    await api.put(`/admin/products/${p.id}`, body); load();
  };
  const del = async (id) => { if (!window.confirm("Delete this product?")) return; await api.delete(`/admin/products/${id}`); toast.success("Product deleted"); load(); };
  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulk = async (action) => {
    const ids = [...sel];
    if (!ids.length) return;
    if (action === "delete" && !window.confirm(`Delete ${ids.length} product(s)?`)) return;
    await api.post("/admin/products/bulk", { ids, action });
    toast.success(`${action === "delete" ? "Deleted" : action === "publish" ? "Published" : "Moved to drafts"} ${ids.length} product(s)`);
    setSel(new Set()); load();
  };
  const bulkTagAction = async (action) => {
    const t = bulkTag.trim();
    if (!t) { toast.error("Enter a tag first"); return; }
    const ids = [...sel];
    if (!ids.length) return;
    await api.post("/admin/products/bulk", { ids, action, tag: t });
    toast.success(`${action === "add-tag" ? "Added" : "Removed"} “${t}” on ${ids.length} product(s)`);
    setBulkTag(""); setSel(new Set()); load();
  };

  const segBtn = (v, label, n) => (
    <button onClick={() => setSeg(v)} data-testid={`cat-seg-${v}`}
      className={`px-4 py-2 rounded-full text-sm font-medium ${seg === v ? "bg-[#0D530E] text-white" : "bg-white border border-black/10"}`}>
      {label}{n != null && n > 0 && <span className="ml-1.5 text-[11px] opacity-80">({n})</span>}
    </button>
  );

  return (
    <div data-testid="admin-catalog">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <SearchBox value={q} onChange={setQ} placeholder="Search catalogue…" />
        <AddBtn label="AI Import" onClick={() => setAiOpen(true)} testid="ai-import-btn" icon={Sparkles} />
        <AddBtn label="Bulk import" onClick={() => setBulkOpen(true)} testid="bulk-import-open-btn" icon={Layers} />
        <AddBtn label="Add manually" onClick={() => setForm({})} testid="add-product-btn" />
        <button onClick={runRemap} disabled={remap.running} data-testid="remap-btn"
          className="flex items-center gap-1.5 text-sm font-semibold border border-[#306D29]/30 text-[#306D29] bg-white px-4 py-2 rounded-full hover:bg-[#306D29]/5 disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>
          {remap.running ? <><Loader2 size={15} className="animate-spin" /> Re-mapping {remap.done}/{remap.total}</> : <><Wand2 size={15} /> AI re-map & tag</>}
        </button>
      </div>
      <div className="flex gap-2 mb-5 items-center">
        {segBtn("all", "All", products.length)}
        {segBtn("published", "Published")}
        {segBtn("draft", "Drafts", draftCount)}
        {list.length > 0 && <button onClick={() => setSel(sel.size === list.length ? new Set() : new Set(list.map((p) => p.id)))} data-testid="cat-select-all" className="ml-auto text-sm font-medium text-[#306D29]">{sel.size === list.length ? "Clear selection" : `Select all (${list.length})`}</button>}
      </div>

      {sel.size > 0 && (
        <div className="flex items-center gap-2 mb-3 bg-[#0D530E] text-[#FBF5DD] rounded-xl px-4 py-2.5" data-testid="bulk-bar">
          <span className="text-sm font-semibold">{sel.size} selected</span>
          <button onClick={() => bulk("publish")} data-testid="bulk-publish" className="ml-auto text-sm font-semibold bg-[#2D7A5D] hover:bg-[#256b50] px-3 py-1.5 rounded-full" style={{ transition: "background-color 0.2s ease" }}>Publish</button>
          <button onClick={() => bulk("draft")} data-testid="bulk-unpublish" className="text-sm font-semibold bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-full" style={{ transition: "background-color 0.2s ease" }}>Unpublish</button>
          <button onClick={() => bulk("delete")} data-testid="bulk-delete" className="text-sm font-semibold bg-[#B23B3B] hover:bg-[#9c3232] px-3 py-1.5 rounded-full" style={{ transition: "background-color 0.2s ease" }}>Delete</button>
          <button onClick={() => setSel(new Set())} className="text-sm text-[#FBF5DD]/70">clear</button>
        </div>
      )}

      <div className="bg-white border border-black/5 rounded-2xl overflow-hidden divide-y divide-black/5">
        {list.length === 0 && <p className="p-5 text-[#5C5C5C]">No products here.</p>}
        {list.map((p) => (
          <div key={p.id} className={`flex items-center gap-3 p-3 ${p.active ? "" : "opacity-55"}`} data-testid={`product-row-${p.id}`}>
            <input type="checkbox" checked={sel.has(p.id)} onChange={() => toggleSel(p.id)} data-testid={`sel-${p.id}`} className="shrink-0 w-4 h-4 accent-[#306D29]" />
            <img src={p.image} alt={p.name} className="w-14 h-14 rounded-xl object-cover bg-[#E7E1B1] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-[#0D530E] truncate flex items-center gap-1.5">
                {p.name}
                {p.featured && <Star size={12} className="text-[#D9933B] fill-[#D9933B]" />}
                {p.status === "draft" && <span className="text-[10px] font-bold uppercase bg-[#D9933B] text-white rounded-full px-1.5 py-0.5">Draft</span>}
              </p>
              <p className="text-xs text-[#5C5C5C] truncate">{p.source_store || p.brand} · {p.category}</p>
              <p className={`text-[10px] truncate ${isStale(p) ? "text-[#B23B3B] font-semibold" : "text-[#5C5C5C]"}`}>{checkedLabel(p)}</p>
            </div>
            <div className="text-right shrink-0 hidden md:block">
              <p className="font-medium text-sm text-[#306D29]">{cedis(p.uk_price_gbp ?? p.price_gbp, fx)}</p>
              <p className="text-[11px] text-[#5C5C5C]">UK £{p.uk_price_gbp ?? p.price_gbp}{p.weight_kg ? ` · ${p.weight_kg}kg` : ""}</p>
            </div>
            <div className="flex items-center gap-0.5 shrink-0">
              {p.purchase_url && <IconBtn title="Refresh price from source link" testid={`reprice-${p.id}`} onClick={() => reprice(p)}><RefreshCw size={15} className={repricing.has(p.id) ? "animate-spin text-[#306D29]" : ""} /></IconBtn>}
              {p.status === "draft" && <IconBtn title="Publish" testid={`publish-${p.id}`} onClick={() => save(p, { status: "published" })}><Eye size={15} className="text-[#2D7A5D]" /></IconBtn>}
              <IconBtn title={p.featured ? "Unfeature" : "Feature"} active={p.featured} testid={`feat-${p.id}`} onClick={() => save(p, { featured: !p.featured })}><Star size={15} /></IconBtn>
              <IconBtn title={p.active ? "Hide" : "Show"} testid={`active-${p.id}`} onClick={() => save(p, { active: !p.active })}>{p.active ? <Eye size={15} /> : <EyeOff size={15} />}</IconBtn>
              <IconBtn title="Edit" testid={`edit-${p.id}`} onClick={() => setForm(p)}><Pencil size={15} /></IconBtn>
              <IconBtn title="Delete" danger testid={`del-${p.id}`} onClick={() => del(p.id)}><Trash2 size={15} /></IconBtn>
            </div>
          </div>
        ))}
      </div>

      {aiOpen && <AIImport stores={stores} onClose={() => setAiOpen(false)} onImported={(prod) => { setAiOpen(false); load(); setForm(prod); }} />}
      {bulkOpen && <BulkImportModal stores={stores} categories={categories} fx={fx} onClose={() => setBulkOpen(false)} onDone={load} />}
      {form && <ProductForm product={form} stores={stores} categories={categories} fx={fx} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
    </div>
  );
}

const CHECK_DAY = 86400000;
function isStale(p) {
  if (!p.last_checked) return false;
  const days = (Date.now() - new Date(p.last_checked).getTime()) / CHECK_DAY;
  return !isNaN(days) && days > 30;
}
function checkedLabel(p) {
  if (!p.last_checked) return "Price not checked yet";
  const t = new Date(p.last_checked).getTime();
  if (isNaN(t)) return "";
  const days = Math.floor((Date.now() - t) / CHECK_DAY);
  if (days <= 0) return "Price checked today";
  return `Price checked ${days}d ago${days > 30 ? " · stale, re-import" : ""}`;
}

function fullBody(f) {
  const imgs = (f.images || []).filter(Boolean);
  const ordered = f.image ? [f.image, ...imgs.filter((u) => u !== f.image)] : imgs;
  return {
    name: f.name, brand: f.brand || "", category: f.category || CATEGORIES[0], subcategory: f.subcategory || "",
    region: "UK", image: f.image || "", images: ordered, description: f.description || "",
    price_gbp: Number(f.price_gbp) || 0, shipping_gbp: 0, handling_gbp: 0,
    delivery_eta: f.delivery_eta || "4-6 weeks", featured: !!f.featured, active: f.active ?? true,
    source_store: f.source_store || "", purchase_url: f.purchase_url || "", customer_price_gbp: null,
    weight_kg: f.weight_kg != null && f.weight_kg !== "" ? Number(f.weight_kg) : null,
    sku: f.sku || "", status: f.status || "published",
    supplier_category: f.supplier_category || "",
    tags: Array.isArray(f.tags) ? f.tags : String(f.tags || "").split(",").map((t) => t.trim().toLowerCase()).filter(Boolean),
    shipping_class: f.shipping_class || "small", oversized: !!f.oversized,
    variants: (f.variants || []).filter((v) => (v.name || "").trim()).map((v) => ({
      id: v.id || (v.name || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      name: (v.name || "").trim(), price_gbp: Number(v.price_gbp) || 0,
      weight_kg: v.weight_kg === "" || v.weight_kg == null ? null : Number(v.weight_kg),
      sku: v.sku || "", image: v.image || "", available: v.available !== false,
    })),
    last_checked: new Date().toISOString().slice(0, 10),
  };
}

function AIImport({ stores, onClose, onImported }) {
  const [store, setStore] = useState(stores[0]?.name || "");
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const run = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/admin/ai/import-product", { store, url });
      toast.success(data.fetched ? "Product imported from page" : "Product drafted from URL — please review");
      onImported(data.product);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI import failed"); setLoading(false);
    }
  };
  return (
    <Modal title="AI Product Import" onClose={onClose} testid="ai-import-modal">
      <form onSubmit={run} className="space-y-4">
        <p className="text-sm text-[#5C5C5C]">Paste a store product page URL. AI extracts the name, price, image, category and suggests an Africa price — saved as a <b>draft</b> for your review.</p>
        <div>
          <label className="text-xs font-semibold text-[#5C5C5C]">Store</label>
          {stores.length > 0 ? (
            <select className={inputCls} value={store} onChange={(e) => setStore(e.target.value)} data-testid="ai-store">
              {stores.map((s) => <option key={s.id}>{s.name}</option>)}
            </select>
          ) : (
            <input className={inputCls} placeholder="e.g. Boots UK" value={store} onChange={(e) => setStore(e.target.value)} data-testid="ai-store" />
          )}
        </div>
        <div>
          <label className="text-xs font-semibold text-[#5C5C5C]">Product URL</label>
          <input required type="url" className={inputCls} placeholder="https://www.boots.com/..." value={url} onChange={(e) => setUrl(e.target.value)} data-testid="ai-url" />
        </div>
        <button type="submit" disabled={loading} data-testid="ai-run"
          className="w-full bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>
          {loading ? <><Loader2 size={17} className="animate-spin" /> Analysing…</> : <><Sparkles size={17} /> Import with AI</>}
        </button>
      </form>
    </Modal>
  );
}

function ProductForm({ product, stores, categories, fx, onClose, onSaved }) {
  const editing = !!product.id;
  const catNames = (categories && categories.length) ? categories.map((c) => c.name) : CATEGORIES;
  const [f, setF] = useState({
    name: product.name || "", brand: product.brand || "", source_store: product.source_store || "", purchase_url: product.purchase_url || "",
    category: product.category || catNames[0], subcategory: product.subcategory || "", image: product.image || "", images: product.images || [], description: product.description || "",
    price_gbp: product.supplier_cost_gbp ?? product.price_gbp ?? "", customer_price_gbp: product.customer_price_gbp ?? "",
    shipping_gbp: product.shipping_gbp ?? 12, handling_gbp: product.handling_gbp ?? 5, weight_kg: product.weight_kg ?? "", sku: product.sku || "",
    delivery_eta: product.delivery_eta || "4-6 weeks", featured: product.featured || false, active: product.active ?? true, status: product.status || "published",
    shipping_class: product.shipping_class || "small", oversized: product.oversized || false,
    variants: product.variants || [],
    supplier_category: product.supplier_category || "", tags: (product.tags || []).join(", "),
  });
  const [saving, setSaving] = useState(false);
  const [genStyle, setGenStyle] = useState("clean");
  const [genLoading, setGenLoading] = useState(false);
  const [addImg, setAddImg] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const addImageUrl = () => {
    const u = (addImg || "").trim();
    if (!u) return;
    setF((prev) => ({
      ...prev,
      image: prev.image || u,
      images: [...(prev.images || []), u].filter((v, i, a) => v && a.indexOf(v) === i),
    }));
    setAddImg("");
  };
  const genImage = async () => {
    if (!f.name) { toast.error("Enter a product name first"); return; }
    setGenLoading(true);
    try {
      const { data } = await api.post("/admin/ai/generate-image", { product_id: product.id || "", name: f.name, category: f.category, style: genStyle });
      setF((prev) => ({ ...prev, image: data.url }));
      toast.success("AI image generated");
    } catch (err) { toast.error(err.response?.data?.detail || "Image generation failed"); }
    finally { setGenLoading(false); }
  };

  const addVariant = () => setF((prev) => ({ ...prev, variants: [...(prev.variants || []), { name: "", price_gbp: prev.price_gbp || "", weight_kg: prev.weight_kg || "", sku: "", image: "", available: true }] }));
  const setVariant = (idx, k, v) => setF((prev) => ({ ...prev, variants: (prev.variants || []).map((x, i) => (i === idx ? { ...x, [k]: v } : x)) }));
  const removeVariant = (idx) => setF((prev) => ({ ...prev, variants: (prev.variants || []).filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) await api.put(`/admin/products/${product.id}`, fullBody(f));
      else await api.post("/admin/products", fullBody(f));
      toast.success(editing ? "Product saved" : "Product added"); onSaved();
    } catch { toast.error("Failed to save product"); setSaving(false); }
  };

  return (
    <Modal title={editing ? "Edit product" : "Add product"} onClose={onClose} testid="product-form">
      <form onSubmit={submit} className="space-y-3">
        <input required className={inputCls} placeholder="Product name" value={f.name} onChange={set("name")} data-testid="pf-name" />
        <div className="grid grid-cols-2 gap-3">
          <input className={inputCls} placeholder="Brand" value={f.brand} onChange={set("brand")} data-testid="pf-brand" />
          <select className={inputCls} value={f.category} onChange={set("category")} data-testid="pf-category">{catNames.map((c) => <option key={c}>{c}</option>)}</select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <select className={inputCls} value={f.subcategory} onChange={set("subcategory")} data-testid="pf-subcategory">
            <option value="">— Sub-category —</option>
            {(categories?.find((c) => c.name === f.category)?.subcategories || []).map((s) => <option key={s}>{s}</option>)}
          </select>
          {stores.length > 0 ? (
            <select className={inputCls} value={f.source_store} onChange={set("source_store")} data-testid="pf-store">
              <option value="">— Source store —</option>
              {stores.map((s) => <option key={s.id}>{s.name}</option>)}
            </select>
          ) : <input className={inputCls} placeholder="Source store" value={f.source_store} onChange={set("source_store")} data-testid="pf-store" />}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input className={inputCls} placeholder="SKU" value={f.sku} onChange={set("sku")} />
          <input className={inputCls} placeholder="Delivery ETA" value={f.delivery_eta} onChange={set("delivery_eta")} />
        </div>
        <div className="flex gap-2 items-center">
          <input className={inputCls} placeholder="Purchase URL (where to buy)" value={f.purchase_url} onChange={set("purchase_url")} data-testid="pf-url" />
          {f.purchase_url && <a href={f.purchase_url} target="_blank" rel="noreferrer" className="text-[#306D29] shrink-0"><ExternalLink size={18} /></a>}
        </div>
        <input required className={inputCls} placeholder="Image URL (cover)" value={f.image} onChange={set("image")} data-testid="pf-image" />
        {f.image && <img src={f.image} alt="" className="w-full h-32 object-cover rounded-xl bg-[#E7E1B1]" />}
        <div className="flex gap-2 items-center">
          <input className={inputCls} placeholder="Add another image URL" value={addImg} onChange={(e) => setAddImg(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addImageUrl(); } }} data-testid="pf-add-image" />
          <button type="button" onClick={addImageUrl} data-testid="pf-add-image-btn"
            className="shrink-0 text-sm font-semibold bg-[#306D29] hover:bg-[#0D530E] text-white px-3 py-2 rounded-lg" style={{ transition: "background-color 0.2s ease" }}>Add</button>
        </div>
        {f.images && f.images.length >= 1 && (
          <div data-testid="pf-gallery">
            <p className="text-xs uppercase tracking-wider text-[#5C5C5C] font-semibold mb-1.5">Gallery ({f.images.length}) · tap to set cover</p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {f.images.map((img, i) => (
                <div key={i} className="relative shrink-0">
                  <button type="button" onClick={() => setF({ ...f, image: img })}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 block ${f.image === img ? "border-[#306D29]" : "border-transparent"}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                  <button type="button" onClick={() => setF({ ...f, images: f.images.filter((_, idx) => idx !== i) })}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#B23B3B] text-white flex items-center justify-center text-xs" title="Remove">×</button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 rounded-xl bg-[#E7E1B1]/40 p-2">
          <Wand2 size={16} className="text-[#306D29] shrink-0 ml-1" />
          <select value={genStyle} onChange={(e) => setGenStyle(e.target.value)} data-testid="pf-img-style" className="flex-1 bg-white border border-black/10 rounded-lg px-2 py-2 text-sm focus:outline-none">
            <option value="clean">Clean product photo</option>
            <option value="lifestyle">Lifestyle image</option>
            <option value="social">Social media image</option>
            <option value="box">Box / delivery scene</option>
          </select>
          <button type="button" onClick={genImage} disabled={genLoading} data-testid="pf-generate-img"
            className="flex items-center gap-1.5 text-sm font-semibold bg-[#306D29] hover:bg-[#0D530E] text-white px-3 py-2 rounded-lg disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>
            {genLoading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate
          </button>
        </div>
        <textarea className={inputCls} rows={2} placeholder="Description" value={f.description} onChange={set("description")} />

        <div className="rounded-xl border border-[#306D29]/15 p-3 space-y-2" data-testid="pf-ucs">
          <p className="text-xs font-semibold text-[#306D29] uppercase tracking-wider">Category mapping & search</p>
          {f.supplier_category && (
            <p className="text-xs text-[#5C5C5C]">Supplier said “<b>{f.supplier_category}</b>” → mapped to <b>{f.category}{f.subcategory ? ` · ${f.subcategory}` : ""}</b></p>
          )}
          <input className={inputCls} placeholder="Supplier's own category (hidden from customers)" value={f.supplier_category} onChange={set("supplier_category")} data-testid="pf-supplier-cat" />
          <div>
            <label className="text-xs font-semibold text-[#5C5C5C]">Search tags (comma separated — e.g. dry skin, family)</label>
            <input className={inputCls} placeholder="dry skin, sensitive, moisturising" value={f.tags} onChange={set("tags")} data-testid="pf-tags" />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-semibold text-[#5C5C5C]">UK store price £ (no markup)</label>
            <input required type="number" step="0.01" className={inputCls} placeholder="14.99" value={f.price_gbp} onChange={set("price_gbp")} data-testid="pf-price" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#5C5C5C]">Weight (kg)</label>
            <input type="number" step="0.01" className={inputCls} placeholder="0.5" value={f.weight_kg} onChange={set("weight_kg")} data-testid="pf-weight" />
            <label className="flex items-center gap-2 mt-2 text-sm text-[#0D530E] cursor-pointer">
              <input type="checkbox" checked={!!f.oversized} onChange={(e) => setF({ ...f, oversized: e.target.checked })} data-testid="pf-oversized" className="w-4 h-4 accent-[#306D29]" />
              Oversized (TV, furniture, bulky) → shipping quote
            </label>
          </div>
        </div>

        <div className="border-t border-black/5 pt-3" data-testid="pf-variants">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-[#5C5C5C]">Variants (size / pack) — each with its own price, weight, SKU</label>
            <button type="button" onClick={addVariant} data-testid="pf-add-variant" className="text-sm font-semibold text-[#306D29] flex items-center gap-1"><Plus size={14} /> Add variant</button>
          </div>
          {(f.variants || []).length === 0 && <p className="text-[11px] text-[#5C5C5C]">No variants — the product uses the price & weight above.</p>}
          <div className="space-y-2">
            {(f.variants || []).map((v, i) => (
              <div key={i} className="bg-[#FBF5DD] rounded-xl p-2.5 space-y-2" data-testid={`pf-variant-${i}`}>
                <div className="flex gap-2">
                  <input className={inputCls} placeholder="Name (e.g. 30ML 2-Pack)" value={v.name} onChange={(e) => setVariant(i, "name", e.target.value)} data-testid={`pf-var-name-${i}`} />
                  <button type="button" onClick={() => removeVariant(i)} className="shrink-0 text-[#B23B3B] px-2"><Trash2 size={15} /></button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <input type="number" step="0.01" className={inputCls} placeholder="UK £" value={v.price_gbp} onChange={(e) => setVariant(i, "price_gbp", e.target.value)} data-testid={`pf-var-price-${i}`} />
                  <input type="number" step="0.01" className={inputCls} placeholder="Weight kg" value={v.weight_kg} onChange={(e) => setVariant(i, "weight_kg", e.target.value)} data-testid={`pf-var-weight-${i}`} />
                  <input className={inputCls} placeholder="SKU" value={v.sku} onChange={(e) => setVariant(i, "sku", e.target.value)} data-testid={`pf-var-sku-${i}`} />
                </div>
                <div className="flex gap-2 items-center">
                  <input className={inputCls} placeholder="Variant image URL (optional)" value={v.image} onChange={(e) => setVariant(i, "image", e.target.value)} data-testid={`pf-var-image-${i}`} />
                  <label className="flex items-center gap-1.5 text-sm text-[#0D530E] shrink-0 cursor-pointer">
                    <input type="checkbox" checked={v.available !== false} onChange={(e) => setVariant(i, "available", e.target.checked)} className="w-4 h-4 accent-[#306D29]" data-testid={`pf-var-avail-${i}`} /> In stock
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="hidden">
          <div />
          <div>
            <label className="text-xs font-semibold text-[#5C5C5C]">Shipping class</label>
            <select className={inputCls} value={f.shipping_class} onChange={set("shipping_class")} data-testid="pf-shipping-class">
              <option value="small">Small item</option>
              <option value="medium">Medium item</option>
              <option value="large">Large / heavy</option>
            </select>
          </div>
        </div>
        <div className="rounded-xl bg-[#E7E1B1]/50 p-3 text-sm" data-testid="pf-price-note">
          <p className="text-[#0D530E]">Customer pays this <b>UK price</b> ({cedis(f.price_gbp, fx)}). Our shopping/handling fee and weight-based shipping are added transparently at checkout — no product markup.</p>
        </div>

        <div className="flex flex-wrap gap-4 items-center">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} /> Featured</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Active</label>
          <select className={`${inputCls} w-auto ml-auto`} value={f.status} onChange={set("status")} data-testid="pf-status">
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
        <button type="submit" disabled={saving} data-testid="pf-submit" className="w-full bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold py-3 rounded-full disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>{saving ? "Saving…" : editing ? "Save product" : "Add product"}</button>
      </form>
    </Modal>
  );
}
